import ProtectedPageHeader from 'components/ProtectedPageHeader'
import Spinner from 'components/Spinner'
import ScrollingTabNav from 'components/Tabs/ScrollingTabNav'
import TabItem from 'components/Tabs/TabItem'
import TabPane from 'components/Tabs/TabPane'
import { useHashTab } from 'components/Tabs/useHashTab'
import useFlag from 'hooks/useFlag'
import useFlagsReady from 'hooks/useFlagsReady'
import { useReportPermission } from 'hooks/useReportPermission'
import { buildTranslate } from 'locales'
import React from 'react'
import Tab from 'react-bootstrap-five/Tab'
import Helmet from 'react-helmet'
import { Redirect } from 'react-router-dom'
import { history } from 'store'
import AverageOrdersPerLocationPerMonthTab from './AverageOrdersPerLocationPerMonthTab'
import ChannelPreferenceTab from './ChannelPreferenceTab'
import DigitalOrdersByHourTab from './DigitalOrdersByHourTab'
import { DIGITAL_ORDERING_FLAG, DIGITAL_ORDERING_PERMISSION } from './gate'
import ItemPopularityInDigitalOrdersTab from './ItemPopularityInDigitalOrdersTab'
import OrdersByClientTab from './OrdersByClientTab'
import OrdersByHandoffModeTab from './OrdersByHandoffModeTab'

const t = buildTranslate('digital_ordering')

export enum Tabs {
  OrdersByClient = 'orders_by_client',
  OrdersByHandoffMode = 'orders_by_handoff_mode',
  AverageOrdersPerLocationPerMonth = 'average_orders_per_location_per_month',
  ChannelPreference = 'channel_preference',
  DigitalOrdersByHour = 'digital_orders_by_hour',
  ItemPopularityInDigitalOrders = 'item_popularity_in_digital_orders',
}

// The shared resolver matches the hash exactly, so no ordering is needed.
const ALL_TABS = Object.values(Tabs)

const DigitalOrderingReport: React.FC = () => {
  const digitalOrderingReportsEnabled = useFlag(DIGITAL_ORDERING_FLAG, false)
  const areFlagsReady = useFlagsReady()

  const [activeTab, setActiveTab] = useHashTab(ALL_TABS, Tabs.OrdersByClient)

  const handleSwitchTab = (tab: Tabs) => {
    history.replace({ ...history.location, hash: tab })
    setActiveTab(tab)
  }

  const reportPermission = useReportPermission(DIGITAL_ORDERING_PERMISSION)

  // Second of the two flag guards; the nav entry in
  // Navbar/Experiences/Content.tsx is the first.
  //
  // The redirect has to wait for LaunchDarkly to evaluate this merchant's
  // context. `useFlag` is `useFlags()[key] ?? defaultValue`, so "flag is off"
  // and "LD has not evaluated yet" both read false — and redirecting during the
  // second case bounces the user to / on every hard refresh of this URL, with
  // no way back once the real value lands. Rendering decisions self-correct
  // when the flag settles; an irreversible redirect does not. Same guard shape
  // as RecoveryAI/index.tsx, and the reason useFlagsReady exists.
  if (!areFlagsReady) {
    return (
      <div role="status" aria-live="polite" aria-label={t('loading')}>
        <Spinner isLoading size="4x" className="text-center margin-top-huge" />
      </div>
    )
  }

  if (!digitalOrderingReportsEnabled) return <Redirect to="/" />

  return (
    <>
      <Helmet>
        <title>{t(`tabs.${activeTab}.page_title`)}</title>
      </Helmet>
      <ProtectedPageHeader
        title={t('title')}
        resource={reportPermission.resource}
        description={t('subtitle')}
        action={reportPermission.action}
      >
        <Tab.Container
          activeKey={activeTab}
          transition={false}
          onSelect={(tab: string | null) => {
            handleSwitchTab(tab as Tabs)
          }}
        >
          <ScrollingTabNav ariaLabel="Digital ordering reports">
            <TabItem
              state={Tabs.OrdersByClient}
              title={t('tabs.orders_by_client.title')}
            />
            <TabItem
              state={Tabs.OrdersByHandoffMode}
              title={t('tabs.orders_by_handoff_mode.title')}
            />
            <TabItem
              state={Tabs.AverageOrdersPerLocationPerMonth}
              title={t('tabs.average_orders_per_location_per_month.title')}
            />
            <TabItem
              state={Tabs.ChannelPreference}
              title={t('tabs.channel_preference.title')}
            />
            <TabItem
              state={Tabs.DigitalOrdersByHour}
              title={t('tabs.digital_orders_by_hour.title')}
            />
            <TabItem
              state={Tabs.ItemPopularityInDigitalOrders}
              title={t('tabs.item_popularity_in_digital_orders.title')}
            />
          </ScrollingTabNav>
          <Tab.Content className="pb-l h-100 grey-05-bkg">
            <TabPane state={Tabs.OrdersByClient} mountOnEnter>
              <OrdersByClientTab />
            </TabPane>
            <TabPane state={Tabs.OrdersByHandoffMode} mountOnEnter>
              <OrdersByHandoffModeTab />
            </TabPane>
            <TabPane state={Tabs.AverageOrdersPerLocationPerMonth} mountOnEnter>
              <AverageOrdersPerLocationPerMonthTab />
            </TabPane>
            <TabPane state={Tabs.ChannelPreference} mountOnEnter>
              <ChannelPreferenceTab />
            </TabPane>
            <TabPane state={Tabs.DigitalOrdersByHour} mountOnEnter>
              <DigitalOrdersByHourTab />
            </TabPane>
            <TabPane state={Tabs.ItemPopularityInDigitalOrders} mountOnEnter>
              <ItemPopularityInDigitalOrdersTab />
            </TabPane>
          </Tab.Content>
        </Tab.Container>
      </ProtectedPageHeader>
    </>
  )
}

export default DigitalOrderingReport
